import React, { useMemo } from 'react';
import { CheckCircle, XCircle } from '@phosphor-icons/react';
import classNames from 'classnames';

import { FaceEmailType } from '../../../../UtilsEmail';

import { EmailSendGroup } from '../../../../components/form';
import { Button } from '../../../../components/general';

import { FACE_EMAIL_STATUS } from '../../../../constants';

import {
  Person,
  MAClips,
  PeopleEmailState,
  Shot,
  WildcardFiller,
} from '../../../../types';
import { isEmpty, getDuration } from '../../../../utils';

interface Props {
  person: Person;
  requiredShots: Shot[];
  maClips: MAClips;
  emailState: PeopleEmailState;
  setEmailState: React.Dispatch<React.SetStateAction<PeopleEmailState>>;
  handleViewClips: () => void;
}

const MaPersonBlock: React.FC<Props> = (props) => {
  const {
    person,
    requiredShots,
    maClips,
    emailState,
    setEmailState,
    handleViewClips,
  } = props;

  const face = person.face;

  const requestBlocks = useMemo(
    () => [
      {
        type: 'required',
        heading: `Request Required (${maClips.missingClips.length ?? 0})`,
        description: 'Request missing clips',
        show: !isEmpty(maClips.missingClips),
        link: maClips.requiredClipsData.share_url,
      },
      {
        type: 'all',
        heading: `Request All (${requiredShots.length})`,
        description: 'Request all clips',
        show: maClips.missingClips.length !== requiredShots.length,
        link: maClips.allClipsData.share_url,
      },
    ],
    [maClips, requiredShots.length],
  );

  return (
    <div className="w-full xl:w-[calc((100%_-_3rem)_/_2)] flex flex-col gap-2 sm:gap-4">
      <div className="flex items-center gap-4">
        <img
          src={person.img}
          className="w-10 min-w-10 h-10 rounded-full object-cover"
          alt={`${face.name} Icon`}
        />

        <h5>{face.name}</h5>
      </div>

      <EmailSendGroup
        components={{ emailInput: true }}
        person={person}
        emailState={emailState}
        setEmailState={setEmailState}
        fieldText="request"
      />

      <div className="flex flex-col sm:flex-row justify-between gap-2 sm:gap-4">
        <div>
          <div className="font-semibold">Clips</div>

          {maClips.selectedClips.map((clip: WildcardFiller) => (
            <div
              key={`${face.elementId}-${clip.wildCardShotIndex}`}
              className={classNames('flex gap-1 items-center', {
                'text-success-900': clip.clipId,
                'text-error-900': !clip.clipId,
              })}
            >
              {clip.clipId ? (
                <CheckCircle size="1.25rem" weight="fill" />
              ) : (
                <XCircle size="1.25rem" weight="fill" />
              )}

              {getDuration(clip.duration ?? 0)}
            </div>
          ))}

          {!isEmpty(person.clips) && (
            <Button
              onClick={handleViewClips}
              variant="link"
              className="mt-1 text-sm"
            >
              View clips
            </Button>
          )}
        </div>

        <div className="hidden xl:block border-l border-gray-300 border-solid" />

        <div className="flex flex-col gap-2">
          {requestBlocks.map((block) => {
            if (!block.show) return null;

            const blockId = `${face.elementId}-${block.type}`;

            return (
              <div key={blockId} className="flex items-center gap-2 sm:gap-4">
                <div className="w-44 min-w-fit mr-auto sm:mr-0">
                  <div className="font-semibold">{block.heading}</div>
                  <div className="text-sm">{block.description}</div>
                </div>

                <EmailSendGroup
                  components={{ sendBtn: 'rec', copyBtn: 'rec' }}
                  type={`missing-clips-${block.type}` as FaceEmailType}
                  person={person}
                  maClips={maClips}
                  emailState={emailState}
                  setEmailState={setEmailState}
                  fieldText="request"
                  link={block.link}
                />
              </div>
            );
          })}

          {[FACE_EMAIL_STATUS.SENT, FACE_EMAIL_STATUS.RESEND].includes(
            emailState[face.elementId].status,
          ) && (
            <div className="text-sm italic opacity-80">
              Email sent successfully
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MaPersonBlock;
